// Helpers to turn catalog products into a size-matrix (one row per colourway/product, sizes as
// columns) and to validate quantities against B2B quantity rules.

const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '2XL', '3XL', '4XL', 'OS', 'ONE SIZE', 'O/S'];

function sizeRank(s) {
  const i = SIZE_ORDER.indexOf(String(s).toUpperCase());
  return i === -1 ? 999 : i;
}

// The size value of a variant (the option named ~"size", else the first option, else its title).
export function sizeOf(variant) {
  const o = variant.options?.find((x) => /size/i.test(x.name));
  return o ? o.value : (variant.options?.[0]?.value ?? variant.title);
}

export function buildMatrix(products) {
  const sizeSet = new Set();
  const rows = products.map((p) => {
    const cells = {};
    for (const v of p.variants) {
      const s = sizeOf(v);
      sizeSet.add(s);
      cells[s] = v;
    }
    return { product: p, cells };
  });
  const columns = [...sizeSet].sort(
    (a, b) => sizeRank(a) - sizeRank(b) || String(a).localeCompare(String(b))
  );
  return { columns, rows };
}

// Returns a short error string if the quantity breaks the rule, else null. Empty/0 is allowed.
// `stockMax` is the client stock cap folded in by capForClient (absent for staff/reps).
export function qtyError(qty, rule) {
  if (!qty) return null;
  const min = rule?.min ?? 1;
  const inc = rule?.increment ?? 1;
  const max = rule?.max ?? null;
  const stockMax = rule?.stockMax ?? null;
  if (stockMax != null && qty > stockMax) return stockMax > 0 ? `Only ${stockMax} available` : 'Sold out';
  if (qty < min) return `Min ${min}`;
  if (max != null && qty > max) return `Max ${max}`;
  if (inc > 1 && qty % inc !== 0) return `Multiples of ${inc}`;
  return null;
}

// Clients can't order more than the warehouse holds after the B2B buffer — the catalog's `maxQty`.
// Fold it into the variant's quantity rule (and lock sizes with too little to meet the minimum) so
// every order screen shows and enforces it the same way. Staff/reps use the catalog as-is: they can
// still place backorders. Catalogs cached before maxQty existed carry no cap (the server still checks).
export function capForClient(variant) {
  if (variant?.maxQty == null) return variant;
  const rule = variant.quantityRule || {};
  return {
    ...variant,
    locked: variant.locked || variant.maxQty < (rule.min ?? 1),
    quantityRule: { ...rule, stockMax: variant.maxQty },
  };
}

/** A whole ordering catalog with the client cap applied; colours with no orderable size are dropped. */
export function capCatalogForClient(data) {
  if (!data?.products) return data;
  const products = data.products
    .map((p) => ({ ...p, variants: p.variants.map(capForClient) }))
    .filter((p) => p.variants.some((v) => !v.locked));
  return { ...data, products };
}

/**
 * What the customer actually owes on an order.
 *
 * `order.subtotal` is GOODS ONLY — before any customer discount and before shipping — while the
 * invoice and the payment link charge `order.payableTotal`. Showing the subtotal under a "Total"
 * heading is what makes a client believe they were overcharged, so every list uses this instead.
 * Until shipping is confirmed the freight is genuinely unknown, so we say so rather than print a
 * number that looks final.
 */
export function fmtOrderTotal(order) {
  if (!order) return '—';
  const total = order.payableTotal != null ? order.payableTotal : order.subtotal;
  const shippingPending = order.shippingState && order.shippingState !== 'CONFIRMED';
  const money = fmtMoney(total, order.currency);
  return shippingPending ? `${money} + shipping` : money;
}

export function fmtMoney(amount, currency) {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch {
    return `${Number(amount).toFixed(2)} ${currency || ''}`.trim();
  }
}
